import { registerLearningMediaCleanup, setLearningMediaActive } from "./learning-media";

export type VoicePreferences={voiceAccent?:"en-GB"|"en-US";voiceGender?:"female"|"male";voiceURI?:string;voiceRate?:number;voiceAutoplay?:boolean};
export type FrenchVoicePreferences={frenchVoiceGender?:"female"|"male";frenchVoiceURI?:string;frenchVoiceRate?:number;frenchVoiceAutoplay?:boolean};

type VoiceGender="female"|"male"|"unknown";
type VoiceQuality="enhanced"|"standard"|"basic";

const femaleHints=["samantha","victoria","karen","moira","tessa","serena","ava","susan","zira","female","martha","fiona","kate","amélie","amelie","audrey","aurélie","aurelie","hortense","marie","julie","virginie"];
const maleHints=["daniel","alex","fred","arthur","oliver","aaron","guy","david","male","rishi","thomas","nicolas","henri","pierre"];
const enhancedHints=["premium","enhanced","natural","neural","wavenet","studio","online","google","microsoft"];
const basicHints=["compact","espeak","festival","novelty","albert","bad news","bahh","bells","boing","bubbles","cellos","good news","jester","junior","organ","superstar","trinoids","whisper","wobble","zarvox"];

let activeUtterance:SpeechSynthesisUtterance|null=null;
let speechRequest=0;
const SPEECH_MEDIA_SOURCE="speech-synthesis";

function languageTag(value:string){return value.trim().replace("_","-").toLowerCase();}
function isFrenchTag(value:string){return /^fr(?:-|$)/i.test(languageTag(value));}
function isEnglishTag(value:string){return /^en(?:-|$)/i.test(languageTag(value));}
function voiceDescription(voice:SpeechSynthesisVoice){return `${voice.name} ${voice.voiceURI}`.toLowerCase();}

export function inferredVoiceGender(voice:SpeechSynthesisVoice):VoiceGender{
  const value=voiceDescription(voice);
  if(femaleHints.some(hint=>value.includes(hint)))return"female";
  if(maleHints.some(hint=>value.includes(hint)))return"male";
  return"unknown";
}

export function voiceQuality(voice:SpeechSynthesisVoice):VoiceQuality{
  const value=voiceDescription(voice);
  if(basicHints.some(hint=>value.includes(hint)))return"basic";
  if(enhancedHints.some(hint=>value.includes(hint)))return"enhanced";
  return"standard";
}

export function voiceQualityLabel(voice:SpeechSynthesisVoice){
  const quality=voiceQuality(voice);
  return quality==="enhanced"?"高清自然":quality==="basic"?"基础音色":"标准音色";
}

function qualityScore(voice:SpeechSynthesisVoice){
  const quality=voiceQuality(voice);
  return quality==="enhanced"?45:quality==="basic"?-80:10;
}

function sortedVoices(predicate:(voice:SpeechSynthesisVoice)=>boolean){
  if(typeof speechSynthesis==="undefined")return[];
  return speechSynthesis.getVoices().filter(predicate).sort((left,right)=>qualityScore(right)-qualityScore(left)||left.name.localeCompare(right.name));
}

export function englishVoices(){return sortedVoices(voice=>isEnglishTag(voice.lang));}
export function frenchVoices(){return sortedVoices(voice=>isFrenchTag(voice.lang));}

function genderScore(voice:SpeechSynthesisVoice,gender:VoiceGender){
  const actual=inferredVoiceGender(voice);
  return actual===gender?35:actual==="unknown"?4:-25;
}

function bestVoice(voices:SpeechSynthesisVoice[],gender:VoiceGender,locale:string){
  const target=languageTag(locale);
  return [...voices].sort((left,right)=>{
    const score=(voice:SpeechSynthesisVoice)=>qualityScore(voice)+genderScore(voice,gender)+(languageTag(voice.lang)===target?30:0)+(voice.localService?3:0)+(voice.default?1:0);
    return score(right)-score(left)||left.name.localeCompare(right.name);
  })[0];
}

export function selectVoice(preferences:VoicePreferences,accentOverride?:"en-GB"|"en-US",genderOverride?:"female"|"male"){
  const accent=accentOverride||preferences.voiceAccent||"en-GB",gender=genderOverride||preferences.voiceGender||"female";
  // Do not silently switch a British lesson to an American voice (or vice versa).
  const voices=englishVoices().filter(voice=>languageTag(voice.lang)===languageTag(accent));
  if(preferences.voiceURI){
    const exact=voices.find(voice=>voice.voiceURI===preferences.voiceURI);
    if(exact&&(inferredVoiceGender(exact)===gender||inferredVoiceGender(exact)==="unknown"))return exact;
  }
  return bestVoice(voices,gender,accent);
}

export function selectFrenchVoice(preferences:FrenchVoicePreferences={},genderOverride?:"female"|"male"){
  const voices=frenchVoices(),gender=genderOverride||preferences.frenchVoiceGender||"female";
  if(preferences.frenchVoiceURI){
    const exact=voices.find(voice=>voice.voiceURI===preferences.frenchVoiceURI);
    // Repair legacy combinations such as "female + Thomas" instead of trusting a stale URI.
    if(exact&&(inferredVoiceGender(exact)===gender||inferredVoiceGender(exact)==="unknown"))return exact;
  }
  // fr-FR is preferred when quality and requested gender are comparable. Other fr-* voices
  // remain valid French fallbacks; an English voice can never enter this candidate set.
  return bestVoice(voices,gender,"fr-FR");
}

function cleanSpeechText(text:string){return text.replace(/\s+/g," ").replace(/\s+([,.;:!?])/g,"$1").trim();}
function clampRate(rate:number){return Math.max(.6,Math.min(1.2,rate));}

function speakWithLockedVoice(text:string,voice:SpeechSynthesisVoice,locale:string,rate:number,language:"en"|"fr"){
  if(typeof speechSynthesis==="undefined"||typeof SpeechSynthesisUtterance==="undefined")return false;
  const normalizedText=cleanSpeechText(text);if(!normalizedText)return false;
  const localeMatches=language==="fr"?isFrenchTag(voice.lang):languageTag(voice.lang)===languageTag(locale);
  if(!localeMatches)return false;

  stopSpeech();
  const request=++speechRequest;
  if(speechSynthesis.paused)speechSynthesis.resume();
  const utterance=new SpeechSynthesisUtterance(normalizedText);
  // Set both properties on every utterance. Never leave `voice` empty, because browsers then
  // fall back to the operating-system default, which is often an English voice.
  utterance.lang=language==="fr"?languageTag(voice.lang).replace(/^fr-fr$/,"fr-FR").replace(/^fr-ca$/,"fr-CA"):locale;
  utterance.voice=voice;
  utterance.rate=clampRate(rate);
  utterance.pitch=1;
  utterance.volume=1;
  utterance.onend=()=>{if(request===speechRequest){activeUtterance=null;setLearningMediaActive(SPEECH_MEDIA_SOURCE,false)}};
  utterance.onerror=()=>{if(request===speechRequest){activeUtterance=null;setLearningMediaActive(SPEECH_MEDIA_SOURCE,false)}};
  // Keep a strong reference: Chromium/WebKit can otherwise collect a queued utterance and
  // resume with the default voice on later clicks.
  activeUtterance=utterance;
  setLearningMediaActive(SPEECH_MEDIA_SOURCE,true);
  speechSynthesis.speak(utterance);
  return true;
}

export function stopSpeech(){
  speechRequest+=1;
  if(activeUtterance){activeUtterance.onend=null;activeUtterance.onerror=null;activeUtterance=null}
  if(typeof speechSynthesis!=="undefined")speechSynthesis.cancel();
  setLearningMediaActive(SPEECH_MEDIA_SOURCE,false);
}

registerLearningMediaCleanup(stopSpeech);

export function speakEnglish(text:string,preferences:VoicePreferences={},options:{accent?:"en-GB"|"en-US";gender?:"female"|"male";rate?:number}={}){
  const accent=options.accent||preferences.voiceAccent||"en-GB";
  const voice=selectVoice(preferences,accent,options.gender);if(!voice)return false;
  return speakWithLockedVoice(text,voice,accent,options.rate??preferences.voiceRate??.92,"en");
}

export function speakFrench(text:string,preferences:FrenchVoicePreferences={},options:{gender?:"female"|"male";rate?:number}={}){
  const voice=selectFrenchVoice(preferences,options.gender);if(!voice||!isFrenchTag(voice.lang))return false;
  return speakWithLockedVoice(text,voice,"fr-FR",options.rate??preferences.frenchVoiceRate??.9,"fr");
}
